import React, { useMemo, useState } from 'react';
import cn from 'classnames';

import Button, { ButtonVariantType, buttonStyles } from '@dealroadshow/uikit/core/components/Button';
import Spinner from '@dealroadshow/uikit/core/components/Loader/Spinner';
import Tooltip from '@dealroadshow/uikit/core/components/Tooltip';
import CheckUserAccess from 'src/finsight/ui/common/components/CheckUserAccess';
import { RestrictedAccessExplanation } from '@/finsight/ui/common/components/Explanations';
import {
  mapUserPlanToSubscribeDescription,
} from '@/finsight/application/User/UserPlan/helpers';
import {
  RestrictedButtonType,
} from '@/finsight/ui/common/components/Explanations/RestrictedAccessExplanation/RestrictedAccessExplanation';
import IconEmailChecked from '@dealroadshow/uikit/core/components/Icon/IconEmailChecked';
import IconEmailAlerts from '@dealroadshow/uikit/core/components/Icon/IconEmailAlerts';
import { useUserPlanContext } from '@/finsight/application/User/UserPlan/UserPlanContext';

import styles from './subscribeButton.scss';
import screenVariables from '@dealroadshow/uikit/core/styles/screen/screen.scss';

const SubscribeButtonPremiumization = ({
  requiredPlan,
  label,
  onClick,
  isSubscribed,
  isFetching,
  restrictedButtonType,
}) => {
  const [isExplanationOpen, setIsExplanationOpen] = useState<boolean>(false);
  const { checkAccessByUserPlan } = useUserPlanContext();

  const subscribeButtonClass = cn(styles.subscribeButtonWrapper, {
    [styles.isDisabled]: isSubscribed,
  }, buttonStyles);
  const subscribeIconClass = cn(styles.icon, {
    [styles.isDisabled]: isSubscribed,
  });

  const description = useMemo<string>(() => (
    `Email digests are available on ${ mapUserPlanToSubscribeDescription[requiredPlan] }.
    ${ restrictedButtonType === RestrictedButtonType.LogIn ? 'Log in for access.' : 'Upgrade your plan for access.' }`
  ), [requiredPlan, restrictedButtonType]);

  return (
    <CheckUserAccess
      checks={ [checkAccessByUserPlan(requiredPlan)] }
      fallback={ (
        <RestrictedAccessExplanation
          title={ label }
          description={ description }
          closeTooltip={ () => {
            setIsExplanationOpen(false);
          } }
          isOpen={ isExplanationOpen }
          actionButtonType={ restrictedButtonType }
        >
          <Button
            dataTest="subscribeButton"
            variant={ ButtonVariantType.text }
            onClick={ () => setIsExplanationOpen(true) }
            className={ subscribeButtonClass }
          >
            { isSubscribed ? <IconEmailChecked className={ subscribeIconClass } /> :
            <IconEmailAlerts className={ subscribeIconClass } /> }
          </Button>
        </RestrictedAccessExplanation>
      ) }
    >
      <>
        { isFetching ? (
          <div className={ styles.spinnerContainer }>
            <Spinner size="small" className={ styles.spinner } />
          </div>
        ) : (
          <Button
            dataTest="subscribeButton"
            variant={ ButtonVariantType.text }
            onClick={ onClick }
            disabled={ isSubscribed }
            className={ subscribeButtonClass }
          >
            <Tooltip
              content={ label }
              containerClassName={ styles.subscribeButton }
              displayMaxWidth={ screenVariables.screenM }
            >
              { isSubscribed ? <IconEmailChecked className={ subscribeIconClass } /> :
              <IconEmailAlerts className={ subscribeIconClass } /> }
            </Tooltip>
          </Button>
        ) }
      </>
    </CheckUserAccess>
  );
};

export default SubscribeButtonPremiumization;
